import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from "@mui/material";
import styled from "@emotion/styled";

const HelperText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const Field = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SaveQuoteAsTemplateDialog = ({ open, onClose, onSave, saving = false, quoteName = "" }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [descriptionTouched, setDescriptionTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(quoteName || "");
        setDescription("");
        setDescriptionTouched(false);
    }, [open, quoteName]);

    const canSave = name.trim().length > 0 && description.trim().length > 0;

    const handleSave = () => {
        if (!canSave) return;
        onSave({ name: name.trim(), description: description.trim() });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Save as quote template</DialogTitle>
            <DialogContent>
                <HelperText variant="body2">
                    This copies the line items, assumptions, inclusions and exclusions from this quote into a reusable template, so the next quote for this kind of engagement carries the same tasks, hours and prices. The template is added to the Templates page, where a manager can apply it to a quote and let the AI redraft from it. Clients never see templates. The original quote is not changed.
                </HelperText>
                <Field
                    fullWidth
                    autoFocus
                    label="Template name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="e.g. Analytics Audit — Standard"
                    helperText="Shown in the template list your team picks from."
                    disabled={saving}
                />
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => setDescriptionTouched(true)}
                    required
                    error={descriptionTouched && !description.trim()}
                    placeholder="e.g. A standard-depth analytics audit: tag review, funnel checks and a written findings report. Not the in-depth version — no custom dashboards."
                    helperText="Required. This is what the AI matches a client's request against, so say what this level of the engagement covers and how it differs from your basic or in-depth version."
                    disabled={saving}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
                    {saving ? "Saving template..." : "Save as template"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SaveQuoteAsTemplateDialog;
